"use client";

import { FormEvent, useState } from "react";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthError, AuthNotice, FieldIcon } from "./shared-ui";

type WorkosResetPasswordFormProps = {
	token: string | null;
	loginHref: string;
};

type ResetResponse = {
	ok?: boolean;
	redirectTo?: string;
	error?: string;
};

export function WorkosResetPasswordForm({ token, loginHref }: WorkosResetPasswordFormProps) {
	const [password, set_password] = useState("");
	const [confirm_password, set_confirm_password] = useState("");
	const [is_submitting, set_is_submitting] = useState(false);
	const [error, set_error] = useState<string | null>(token ? null : "This reset link is invalid or has expired.");
	const [notice, set_notice] = useState<string | null>(null);

	async function handle_submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		set_error(null);
		set_notice(null);

		if (!token) {
			set_error("This reset link is invalid or has expired.");
			return;
		}

		if (password.length < 8) {
			set_error("Enter a new password with at least 8 characters.");
			return;
		}

		if (password !== confirm_password) {
			set_error("The passwords do not match.");
			return;
		}

		set_is_submitting(true);

		try {
			const response = await fetch("/api/auth/workos/password-reset/confirm", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					token,
					password
				})
			});
			const payload = (await response.json().catch(() => null)) as ResetResponse | null;

			if (!response.ok || !payload?.ok) {
				set_error(payload?.error ?? "We could not reset your password. Please try again.");
				return;
			}

			set_notice("Your password has been reset. Redirecting you to sign in.");
			window.location.assign(payload.redirectTo ?? loginHref);
		} catch {
			set_error("We could not reach the sign-in service. Please try again.");
		} finally {
			set_is_submitting(false);
		}
	}

	return (
		<form
			onSubmit={handle_submit}
			className="space-y-4"
		>
			<AuthError message={error} />
			<AuthNotice message={notice} />
			<div className="space-y-2">
				<Label htmlFor="new-password">New password</Label>
				<div className="relative">
					<FieldIcon>
						<Lock className="size-4" />
					</FieldIcon>
					<Input
						id="new-password"
						type="password"
						value={password}
						onChange={(event) => set_password(event.target.value)}
						autoComplete="new-password"
						className="h-11 rounded-lg bg-white pl-10 text-base"
						placeholder="Enter a new password"
						minLength={8}
						required
						disabled={!token || is_submitting}
					/>
				</div>
			</div>
			<div className="space-y-2">
				<Label htmlFor="confirm-password">Confirm password</Label>
				<div className="relative">
					<FieldIcon>
						<Lock className="size-4" />
					</FieldIcon>
					<Input
						id="confirm-password"
						type="password"
						value={confirm_password}
						onChange={(event) => set_confirm_password(event.target.value)}
						autoComplete="new-password"
						className="h-11 rounded-lg bg-white pl-10 text-base"
						placeholder="Repeat your new password"
						minLength={8}
						required
						disabled={!token || is_submitting}
					/>
				</div>
			</div>
			<Button
				type="submit"
				disabled={!token || is_submitting}
				className="h-11 w-full rounded-lg bg-[#235a96] text-white hover:bg-[#1d4d82]"
			>
				{is_submitting ? <Loader2 className="size-4 animate-spin" /> : null}
				Reset password
			</Button>
			<Button
				type="button"
				variant="ghost"
				className="h-10 w-full text-slate-600"
				onClick={() => {
					window.location.assign(loginHref);
				}}
			>
				<ArrowLeft className="size-4" />
				Back to login
			</Button>
		</form>
	);
}
